import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useQuery } from '@apollo/client';
import { Modal, Loader, Message } from 'semantic-ui-react';
import { REPORT_JOB_TRANSCRIPT } from '../../../queries/project';

const MIN_MATCH_WORDS = 4;

// Overrides the global `pre` rule in _zoomInfo.scss, which caps any <pre> at
// max-height: 35vh with its own scrollbar — that fights the modal's own
// scroll area, leaving most of the modal's height empty above a short,
// independently-scrolling text box.
const TRANSCRIPT_PRE_STYLE = {
  whiteSpace: 'pre-wrap', maxHeight: 'none', overflow: 'visible', background: 'none', padding: 0,
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const tokenizeWords = (text) => text.match(/[A-Za-z0-9']+/g) || [];

// Matches a run of word tokens in order, with any run of non-word characters
// allowed between them, so punctuation drift between the quote and the
// transcript (a stray period, a comma the model added, curly vs. straight
// quotes) can't break the match the way an exact substring match would.
const regexForWords = (words) => new RegExp(words.map(escapeRegExp).join('\\W+'), 'i');

// A quote segment is often a paraphrase, not a literal transcription — the
// model may drop a filler word or lightly reword mid-sentence even within
// one spliced fragment. Rather than requiring the whole segment to match
// verbatim, search from the longest possible word-window down to
// MIN_MATCH_WORDS and highlight the longest contiguous run that actually
// does appear, so a partial rewording still gets a useful, honest highlight
// instead of no highlight at all.
const findLongestMatchingWindow = (transcript, words) => {
  for (let windowSize = words.length; windowSize >= MIN_MATCH_WORDS; windowSize -= 1) {
    for (let start = 0; start + windowSize <= words.length; start += 1) {
      const match = regexForWords(words.slice(start, start + windowSize)).exec(transcript);
      if (match) {
        return { start: match.index, end: match.index + match[0].length };
      }
    }
  }
  return null;
};

// The model frequently splices together non-adjacent parts of a transcript
// (e.g. a question and its answer, or the start/end of a long response)
// into one quote, joined by "..." — those elisions never appear verbatim in
// the transcript, so a whole-quote match would always fail. Splitting on the
// ellipsis and matching each piece independently lets each spliced fragment
// still get found and highlighted. Bracketed asides like "[are FFPE]" are
// the model's own inserted clarifications, not verbatim speech, so they're
// stripped before matching too.
const splitQuoteSegments = (quote) => String(quote || '')
  .split(/\.{3,}|…/)
  .map((segment) => segment.replace(/\[[^\]]*\]/g, ' ').trim())
  .filter((segment) => segment.split(/\s+/).filter(Boolean).length >= 2);

const findQuoteMatches = (transcript, quote) => {
  const matches = splitQuoteSegments(quote)
    .map((segment) => findLongestMatchingWindow(transcript, tokenizeWords(segment)))
    .filter(Boolean);

  matches.sort((a, b) => a.start - b.start);
  return matches;
};

const TranscriptContent = ({ transcript, quote }) => {
  const highlightRef = useRef(null);
  const matches = useMemo(() => findQuoteMatches(transcript, quote), [transcript, quote]);

  useEffect(() => {
    if (!highlightRef.current) return undefined;

    // The modal continuously recalculates its scrolling/position classes via
    // requestAnimationFrame right after mount (semantic-ui-react's Modal).
    // Scrolling immediately on mount can run before that settles, so the
    // container isn't scrollable yet and the call is a no-op — most visible
    // on a re-open, where the transcript query resolves from cache and
    // mounts this content on the very first frame. Deferring two frames lets
    // the modal finish sizing itself first.
    let innerFrame;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => {
        highlightRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      if (innerFrame) cancelAnimationFrame(innerFrame);
    };
  }, [transcript, quote]);

  if (matches.length === 0) {
    return <pre style={TRANSCRIPT_PRE_STYLE}>{transcript}</pre>;
  }

  const nodes = [];
  let cursor = 0;
  let highlightRefAssigned = false;

  matches.forEach((matchRange) => {
    if (matchRange.start < cursor) return;
    if (matchRange.start > cursor) nodes.push(transcript.slice(cursor, matchRange.start));

    nodes.push(
      <mark key={`match-${matchRange.start}`} ref={highlightRefAssigned ? undefined : highlightRef}>
        {transcript.slice(matchRange.start, matchRange.end)}
      </mark>,
    );
    highlightRefAssigned = true;
    cursor = matchRange.end;
  });

  if (cursor < transcript.length) nodes.push(transcript.slice(cursor));

  return <pre style={TRANSCRIPT_PRE_STYLE}>{nodes}</pre>;
};

const TranscriptModal = ({
  projectId, jobId, quote, children,
}) => {
  const [open, setOpen] = useState(false);

  const { loading, error, data } = useQuery(REPORT_JOB_TRANSCRIPT, {
    skip: !open,
    variables: { projectId, jobId },
  });

  const recordings = data?.reportJob?.zoomMeeting?.meetingReport?.recordings || [];
  const transcript = recordings
    .filter((recording) => recording.contentType === 'Transcript' && recording.content)
    .map((recording) => recording.content.trim())
    .join('\n\n');

  return (
    <Modal
      closeIcon
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      trigger={children}
      size="large"
    >
      <Modal.Header>Interview Transcript</Modal.Header>
      <Modal.Content style={{ height: 'calc(100vh - 150px)', overflowY: 'auto' }}>
        {loading && <Loader active inline="centered" content="Loading transcript..." />}
        {!loading && error && (
          <Message negative>
            <Message.Header>Failed to load transcript</Message.Header>
            <p>{error.message}</p>
          </Message>
        )}
        {!loading && !error && !transcript && (
          <Message content="No transcript is available for this job." />
        )}
        {!loading && !error && transcript && (
          <TranscriptContent transcript={transcript} quote={quote} />
        )}
      </Modal.Content>
    </Modal>
  );
};

export default TranscriptModal;
